import { ChangeEvent, useEffect, useRef, useState } from "react";
import { TastingAnswer } from "store/reducers/answers/reducer";
import { TastingEvent } from "store/reducers/events/reducer";

export type UseTastingRoundArgs = {
  event: TastingEvent | undefined;
  answer: TastingAnswer | undefined;
  roundIndex: number;
};

/**
 * Owns every derivation, controlled-field, and handler the round form needs.
 *
 * The TastingDetails dialog used to inline ~120 lines of state derivation +
 * useEffect choreography. None of that is testable without rendering the
 * dialog. Pulling it into a plain hook makes each rule (asterisk quota,
 * double-asterisk gating, lotto-clear-on-edit, reminder/notes mode gating)
 * exercisable from a tiny renderHook test, no Redux store required.
 */
const useTastingRound = ({ event, answer, roundIndex }: UseTastingRoundArgs) => {
  const draft = useRef({ answer, roundIndex, eventId: event?.id });
  const draftAnswer = draft.current.answer;
  // Compare edits with the answer that seeded this draft, even when a newer
  // server answer arrives while the user is typing.
  const initialSelectedBeer = (draftAnswer?.beers ?? [])[roundIndex] ?? null;
  const initialSelectedRating = (draftAnswer?.ratings ?? [])[roundIndex] ?? null;
  const initialAsterisked = Boolean(
    (draftAnswer?.asterisks || [])[roundIndex] || null
  );
  const initialDoubleAsterisked = Boolean(
    (draftAnswer?.doubleAsterisks || [])[roundIndex] || null
  );
  const initialSecondaryBeer =
    (draftAnswer?.secondaryBeers ?? [])[roundIndex] ?? null;
  const initialNotes = (draftAnswer?.notes ?? [])[roundIndex] ?? "";
  const changesMade = (answer?.changes || [])[roundIndex] || 0;

  const reminderMode = event?.reminderMode ?? "off";
  const showReminder = reminderMode === "both" || reminderMode === "secondary";
  const showNotes = reminderMode === "both" || reminderMode === "notes";

  const [selectedBeer, setSelectedBeer] = useState(initialSelectedBeer);
  const [selectedRating, setSelectedRating] = useState(initialSelectedRating);
  const [selectedAsterisked, setSelectedAsterisked] =
    useState(initialAsterisked);
  const [selectedDoubleAsterisked, setSelectedDoubleAsterisked] = useState(
    initialDoubleAsterisked
  );
  const [selectedSecondaryBeer, setSelectedSecondaryBeer] = useState<
    string | null
  >(initialSecondaryBeer);
  const [selectedNotes, setSelectedNotes] = useState<string>(initialNotes);

  const changes =
    initialSelectedBeer !== selectedBeer ||
    initialSelectedRating !== selectedRating ||
    initialAsterisked !== selectedAsterisked ||
    initialDoubleAsterisked !== selectedDoubleAsterisked ||
    (showReminder && initialSecondaryBeer !== selectedSecondaryBeer) ||
    (showNotes && initialNotes !== selectedNotes);

  useEffect(() => {
    const sameRound = draft.current.roundIndex === roundIndex &&
      draft.current.eventId === event?.id;
    if (sameRound && changes) return;
    draft.current = { answer, roundIndex, eventId: event?.id };
    setSelectedBeer(answer?.beers?.[roundIndex] ?? null);
    setSelectedRating(answer?.ratings?.[roundIndex] ?? null);
    setSelectedAsterisked(Boolean(answer?.asterisks?.[roundIndex]));
    setSelectedDoubleAsterisked(Boolean(answer?.doubleAsterisks?.[roundIndex]));
    setSelectedSecondaryBeer(answer?.secondaryBeers?.[roundIndex] ?? null);
    setSelectedNotes(answer?.notes?.[roundIndex] ?? "");
    // Input edits must not trigger reinitialization. Only a new server answer
    // or a different round can refresh an untouched draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answer, event?.id, roundIndex]);

  const handleClickBeer = (beer: string | null) => {
    setSelectedBeer(selectedBeer === beer ? null : beer);
  };

  const handleRatingChange = (rating: number | null) => {
    setSelectedRating(rating);
  };

  const handleAsteriskedChange = (e: ChangeEvent<HTMLInputElement>) => {
    const asterisked = e?.target?.checked;
    setSelectedAsterisked(asterisked);
    // Clearing the regular asterisk also clears the double asterisk — they
    // are additive and double cannot stand alone.
    if (!asterisked) {
      setSelectedDoubleAsterisked(false);
    }
  };

  const handleDoubleAsteriskedChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSelectedDoubleAsterisked(e?.target?.checked);
  };

  const beerPreselectedIndex = (beer: string | null) => {
    const index = answer?.beers?.indexOf(beer);
    const isBeerOfRound = beer === initialSelectedBeer;
    const notFound = index === undefined;
    return isBeerOfRound || notFound ? -1 : index;
  };

  const isBeerSelected = (beer: string | null) => selectedBeer === beer;

  /**
   * A regular asterisk consumes 1 slot from `asterisksAllowed`. A double
   * asterisk consumes 2 — the regular bump it sits on top of, plus an
   * extra for the doubling. So the quota usage for a round is
   * `asterisk + doubleAsterisk` (each true counts as 1).
   */
  const asterisksLeft = (): number => {
    let total = 0;
    const len = answer?.asterisks?.length ?? 0;
    for (let i = 0; i < len; i++) {
      const isCurrent = i === roundIndex;
      const ast = isCurrent
        ? selectedAsterisked
        : answer?.asterisks?.[i] === true;
      const dbl = isCurrent
        ? selectedAsterisked && selectedDoubleAsterisked
        : answer?.doubleAsterisks?.[i] === true;
      total += (ast ? 1 : 0) + (dbl ? 1 : 0);
    }
    return (event?.asterisksAllowed ?? 0) - total;
  };

  const eventAllowsDouble =
    Boolean(event?.doubleAsterisk) && (event?.asterisksAllowed ?? 0) >= 2;
  // Double can be turned on as long as the quota has room for the extra slot
  // it will consume on top of the regular asterisk.
  const doubleDisabled = !selectedDoubleAsterisked && asterisksLeft() <= 0;

  /**
   * Build the answer payload to dispatch from a Save action. Encapsulates
   * the cross-round merge so the component doesn't have to remember the
   * lotto-clear-on-edit + double-only-with-asterisk safety rules.
   */
  const buildSavePayload = (): TastingAnswer => {
    // Double can only be true when the regular asterisk is on. UI keeps these
    // in sync, but enforce here too as a safety net for persistence.
    const persistedDouble = selectedAsterisked && selectedDoubleAsterisked;
    const baseDoubles =
      answer?.doubleAsterisks ?? (answer?.asterisks?.map(() => false) ?? []);
    const totalRounds = answer?.beers?.length ?? answer?.rounds ?? 0;
    const baseSecondary =
      answer?.secondaryBeers ?? new Array(totalRounds).fill(null);
    const baseNotes = answer?.notes ?? new Array(totalRounds).fill(null);
    const baseLotto =
      answer?.lottoLoco ?? new Array(totalRounds).fill(false);
    return {
      beers: answer?.beers?.map((b, i) =>
        i === roundIndex ? selectedBeer : b
      ),
      ratings: answer?.ratings?.map((r, i) =>
        i === roundIndex ? selectedRating : r
      ),
      asterisks: answer?.asterisks?.map((a, i) =>
        i === roundIndex ? selectedAsterisked : a
      ),
      doubleAsterisks: baseDoubles.map((d, i) =>
        i === roundIndex ? persistedDouble : Boolean(d)
      ),
      // Manually editing a round overrides any random pick — drop the
      // LOTTO badge for that round.
      lottoLoco: baseLotto.map((l, i) =>
        i === roundIndex ? false : Boolean(l)
      ),
      changes: answer?.changes?.map((a, i) =>
        i === roundIndex ? a + 1 : a
      ),
      ...(showReminder
        ? {
            secondaryBeers: baseSecondary.map((b, i) =>
              i === roundIndex ? selectedSecondaryBeer : b
            ),
          }
        : {}),
      ...(showNotes
        ? {
            notes: baseNotes.map((n, i) =>
              i === roundIndex ? selectedNotes.trim() || null : n
            ),
          }
        : {}),
    } as TastingAnswer;
  };

  return {
    // Controlled fields
    selectedBeer,
    selectedRating,
    selectedAsterisked,
    selectedDoubleAsterisked,
    selectedSecondaryBeer,
    setSelectedSecondaryBeer,
    selectedNotes,
    setSelectedNotes,
    // Derived flags
    changes,
    changesMade,
    showReminder,
    showNotes,
    eventAllowsDouble,
    doubleDisabled,
    // Functions
    asterisksLeft,
    handleClickBeer,
    handleRatingChange,
    handleAsteriskedChange,
    handleDoubleAsteriskedChange,
    beerPreselectedIndex,
    isBeerSelected,
    buildSavePayload,
  };
};

export default useTastingRound;
